import { useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

export type Loaded<T> = {
  loading: boolean;
  error?: Error;
  data?: T;
  /** The backend refused the reader, in a way `refused` recognised. */
  refused?: boolean;
};

/**
 * How often a card asks again while something it shows is still running
 * (specs/component-pipeline.md AC-4). The backend answers a repository from the
 * last minute, so asking more often would only repeat the same answer.
 */
export const REFRESH_MS = 60_000;

/**
 * One GET against the `github-insights` backend, which answers from the portal's
 * GitHub App — no reader needs a GitHub login of their own. `plugin` asks
 * another backend plugin instead, such as the `proxy`.
 *
 * Each card calls this for itself, so one source failing leaves the others
 * standing (specs/component-pipeline.md AC-12). `path` undefined means "not yet
 * known what to ask", and keeps the card loading rather than failing.
 *
 * `refreshWhile` is asked of every answer: while it says yes, the card asks
 * again every REFRESH_MS as long as the browser tab is visible — so a pipeline
 * that has finished, or a tab nobody looks at, costs nothing. `refreshKey`
 * asks again once whenever it changes. Either way
 * the card keeps what it shows until the new answer arrives, and a refresh that
 * fails leaves the last answer standing rather than blanking the card: it is at
 * most a minute old, and the next tick asks again.
 *
 * `notFound` turns a 404 whose body it recognises into an answer of `null`,
 * for a backend that says "there is nothing by that name" that way. Any other
 * 404 stays an error: a route or a proxy endpoint that is missing is not an
 * answer.
 *
 * `refused` does the same for a 403 whose body it recognises, answering
 * `refused: true` instead of data — for a route that shows its answer to some
 * readers only and says so, such as the security findings (DX-587).
 */
export function useInsights<T>(
  path: string | undefined,
  options: {
    refreshWhile?: (data: T) => boolean;
    refreshKey?: string;
    plugin?: string;
    notFound?: (body: unknown) => boolean;
    refused?: (body: unknown) => boolean;
  } = {},
): Loaded<T> {
  const {
    refreshWhile,
    refreshKey,
    plugin = 'github-insights',
    notFound,
    refused,
  } = options;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<Loaded<T>>({ loading: true });
  const refreshMs =
    refreshWhile && state.data !== undefined && refreshWhile(state.data)
      ? REFRESH_MS
      : undefined;
  const [tick, setTick] = useState(0);
  const shownPath = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!refreshMs) return undefined;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') setTick(t => t + 1);
    }, refreshMs);
    return () => window.clearInterval(id);
  }, [refreshMs]);

  useEffect(() => {
    if (!path) {
      shownPath.current = undefined;
      setState({ loading: true });
      return undefined;
    }
    let active = true;
    // A new question shows its own loading state; asking the same one again
    // keeps the last answer on screen.
    const background = shownPath.current === path;
    shownPath.current = path;
    if (!background) setState({ loading: true });
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl(plugin);
        const res = await fetchApi.fetch(`${baseUrl}${path}`);
        if (res.status === 404 && notFound) {
          const body: unknown = await res.json().catch(() => undefined);
          if (notFound(body)) {
            if (active) setState({ loading: false, data: null as T });
            return;
          }
        }
        if (res.status === 403 && refused) {
          const body: unknown = await res.json().catch(() => undefined);
          if (refused(body)) {
            if (active) setState({ loading: false, refused: true });
            return;
          }
        }
        if (!res.ok) {
          throw new Error(`${res.status} ${res.statusText}`.trim());
        }
        const data = (await res.json()) as T;
        if (active) setState({ loading: false, data });
      } catch (err) {
        if (active && !background) {
          setState({ loading: false, error: err as Error });
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [
    path,
    tick,
    refreshKey,
    plugin,
    notFound,
    refused,
    discoveryApi,
    fetchApi,
  ]);

  return state;
}

export const slugQuery = (slug: string) =>
  `projectSlug=${encodeURIComponent(slug)}`;
